import { useState } from 'react'
import { createBrowserRouter } from 'react-router'
import { useParams, useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { getDbConnectionList, type DbConnectionInfo } from '@/services/data-integration-api'
import ProtectedLayout from '@/layouts/ProtectedLayout'
import Login from '@/pages/Login'
import Home from '@/pages/Home'
import NotFound from '@/pages/NotFound'
import ConnectionManager from '@/pages/data-integration/ConnectionManager'
import CatalogGateway from '@/pages/data-integration/CatalogGateway'
import TableCatalog from '@/pages/data-integration/TableCatalog'
import TableDetail from '@/pages/data-integration/TableDetail'
import ErDiagram from '@/pages/data-integration/ErDiagram'
import TableMapping from '@/pages/data-integration/TableMapping'
import SyncGlobal from '@/pages/data-integration/SyncGlobal'
import SyncDashboard from '@/pages/data-integration/SyncDashboard'
import AuditGateway from '@/pages/data-integration/AuditGateway'
import DataAudit from '@/pages/data-integration/DataAudit'
import ProdWriteConfig from '@/pages/data-integration/ProdWriteConfig'
import ProdWriteTask from '@/pages/data-integration/ProdWriteTask'
import NormalizedCatalog from '@/pages/data-integration/NormalizedCatalog'

// Router wrappers to bridge props-based pages with route params

function InvalidParam({ message }: { message: string }) {
  const navigate = useNavigate()
  return (
    <div className="p-6 text-center">
      <p className="text-destructive mb-4">{message}</p>
      <Button variant="outline" onClick={() => navigate('/data-integration/connections')}>
        返回连接管理
      </Button>
    </div>
  )
}

function TableCatalogRoute() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const navigate = useNavigate()
  if (!connectionId) return <InvalidParam message="缺少连接ID参数" />
  return (
    <TableCatalog
      connectionId={connectionId}
      onSelectTable={(id) => navigate(`/data-integration/table/${id}`)}
      onViewEr={(connId) => navigate(`/data-integration/er/${connId}`)}
    />
  )
}

function TableDetailRoute() {
  const { tableCatalogId } = useParams<{ tableCatalogId: string }>()
  const navigate = useNavigate()
  const [targetDialogOpen, setTargetDialogOpen] = useState(false)
  const [targetConnections, setTargetConnections] = useState<DbConnectionInfo[]>([])

  if (!tableCatalogId) return <InvalidParam message="缺少表ID参数" />

  const handleCreateMapping = async (id: string) => {
    try {
      const result = await getDbConnectionList({ connection_type: 'target' })
      const targets = result.items
      if (targets.length === 0) {
        alert('暂无目标库连接，请先在连接管理中添加目标库连接')
        return
      }
      if (targets.length === 1) {
        navigate(`/data-integration/mapping/${id}?targetConnectionId=${targets[0]._id}`)
        return
      }
      setTargetConnections(targets)
      setTargetDialogOpen(true)
    } catch (e: any) {
      alert('获取目标库失败: ' + e.message)
    }
  }

  return (
    <>
      <TableDetail
        tableCatalogId={tableCatalogId}
        onBack={() => navigate(-1)}
        onCreateMapping={handleCreateMapping}
      />
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>选择目标库</DialogTitle>
            <DialogDescription>请选择要创建规范存取表的目标数据库连接</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            {targetConnections.map(conn => (
              <Button
                key={conn._id}
                variant="outline"
                className="justify-start"
                onClick={() => {
                  setTargetDialogOpen(false)
                  navigate(`/data-integration/mapping/${tableCatalogId}?targetConnectionId=${conn._id}`)
                }}
              >
                <span className="font-medium">{conn.connection_name}</span>
                <span className="ml-2 text-muted-foreground text-xs">{conn.host}:{conn.port}/{conn.database_name}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function ErDiagramRoute() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const navigate = useNavigate()
  if (!connectionId) return <InvalidParam message="缺少连接ID参数" />
  return (
    <ErDiagram
      connectionId={connectionId}
      onBack={() => navigate(-1)}
    />
  )
}

function TableMappingRoute() {
  const { tableCatalogId } = useParams<{ tableCatalogId: string }>()
  const navigate = useNavigate()
  if (!tableCatalogId) return <InvalidParam message="缺少表ID参数" />
  const searchParams = new URLSearchParams(window.location.search)
  const targetConnectionId = searchParams.get('targetConnectionId') || ''
  return (
    <TableMapping
      tableCatalogId={tableCatalogId}
      targetConnectionId={targetConnectionId}
      onBack={() => navigate(-1)}
    />
  )
}

function SyncDashboardRoute() {
  const { normalizedTableId } = useParams<{ normalizedTableId: string }>()
  const navigate = useNavigate()
  if (!normalizedTableId) return <InvalidParam message="缺少规范存取表ID参数" />
  return (
    <SyncDashboard
      normalizedTableId={normalizedTableId}
      onBack={() => navigate(-1)}
    />
  )
}

function ProdWriteTaskRoute() {
  const { writeTaskId } = useParams<{ writeTaskId: string }>()
  const navigate = useNavigate()
  if (!writeTaskId) return <InvalidParam message="缺少写入任务ID参数" />
  return (
    <ProdWriteTask
      writeTaskId={writeTaskId}
      onBack={() => navigate(-1)}
    />
  )
}

function ProdWriteConfigRoute() {
  const { normalizedTableId } = useParams<{ normalizedTableId: string }>()
  const navigate = useNavigate()
  if (!normalizedTableId) return <InvalidParam message="缺少规范存取表ID参数" />
  return (
    <ProdWriteConfig
      normalizedTableId={normalizedTableId}
      onBack={() => navigate(-1)}
    />
  )
}

function DataAuditRoute() {
  const { normalizedTableId } = useParams<{ normalizedTableId: string }>()
  const navigate = useNavigate()
  if (!normalizedTableId) return <InvalidParam message="缺少规范存取表ID参数" />
  return (
    <DataAudit
      normalizedTableId={normalizedTableId}
      onBack={() => navigate(-1)}
    />
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      { index: true, element: <Home /> },
      // Data Integration routes — gateway (exact) routes before parameterized routes
      { path: 'data-integration/connections', element: <ConnectionManager /> },
      { path: 'data-integration/catalog', element: <CatalogGateway /> },
      { path: 'data-integration/catalog/:connectionId', element: <TableCatalogRoute /> },
      { path: 'data-integration/table/:tableCatalogId', element: <TableDetailRoute /> },
      { path: 'data-integration/er/:connectionId', element: <ErDiagramRoute /> },
      { path: 'data-integration/mapping/:tableCatalogId', element: <TableMappingRoute /> },
      { path: 'data-integration/sync', element: <SyncGlobal /> },
      { path: 'data-integration/sync/:normalizedTableId', element: <SyncDashboardRoute /> },
      { path: 'data-integration/prod-write/:normalizedTableId', element: <ProdWriteConfigRoute /> },
      { path: 'data-integration/prod-write-task/:writeTaskId', element: <ProdWriteTaskRoute /> },
      { path: 'data-integration/audit', element: <AuditGateway /> },
      { path: 'data-integration/audit/:normalizedTableId', element: <DataAuditRoute /> },
      { path: 'data-integration/normalized-catalog', element: <NormalizedCatalog /> },
      // Catch-all 404
      { path: '*', element: <NotFound /> },
    ],
  },
])
